import type { BillingAccountInvoice, InvoiceRecord } from "@warrenio/api-spec/spec.oats.gen";

function isNonZeroAmount(value: number | undefined): value is number {
    return typeof value === "number" && Number.isFinite(value) && value !== 0;
}

export function getSignedDiscountAmount(discountAmount: number | undefined): number {
    if (!isNonZeroAmount(discountAmount)) {
        return 0;
    }

    return discountAmount > 0 ? -discountAmount : discountAmount;
}

export function getInvoiceDiscountAmount(invoice: BillingAccountInvoice): number {
    return getSignedDiscountAmount(invoice.totals.discount_amount);
}

export function getRecordDiscountAmount(record: InvoiceRecord): number {
    return getSignedDiscountAmount(record.discount_amount);
}

export function getRecordLineTotal(record: InvoiceRecord): number {
    return record.amount + getRecordDiscountAmount(record);
}

export function hasAppliedRecordDiscount(record: InvoiceRecord): boolean {
    return isNonZeroAmount(getRecordDiscountAmount(record));
}

export function getRecordDiscountPercentage(record: InvoiceRecord): number | undefined {
    if (!hasAppliedRecordDiscount(record)) {
        return undefined;
    }

    return isNonZeroAmount(record.discount_percentage) ? record.discount_percentage : undefined;
}

export function hasAnyInvoiceDiscount(invoice: BillingAccountInvoice): boolean {
    return (
        isNonZeroAmount(getInvoiceDiscountAmount(invoice)) ||
        invoice.records_list.some((record) => hasAppliedRecordDiscount(record))
    );
}

export function getUniformRecordDiscountPercentage(invoice: BillingAccountInvoice): number | undefined {
    const firstRecord = invoice.records_list[0];
    if (!firstRecord) {
        return undefined;
    }

    const firstPercentage = getRecordDiscountPercentage(firstRecord);
    if (!isNonZeroAmount(firstPercentage)) {
        return undefined;
    }

    return invoice.records_list.every((record) => getRecordDiscountPercentage(record) === firstPercentage)
        ? firstPercentage
        : undefined;
}

export function getInvoiceDiscountPercentage(invoice: BillingAccountInvoice): number | undefined {
    if (!hasAnyInvoiceDiscount(invoice)) {
        return undefined;
    }

    if (invoice.has_record_level_discount) {
        return getUniformRecordDiscountPercentage(invoice);
    }

    return isNonZeroAmount(invoice.discount_percentage) ? invoice.discount_percentage : undefined;
}
